import { centsToDollars, parsePrice } from "helpers/price";
import { MarketPriceSetting } from "types/marketPrice";
import { IMarketPriceReviewResponse } from "types/marketPriceAnalysis";

/** Starts a browser download of `csvContent`. */
export function downloadCsvFile(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToCSV(data: any[], filename: string) {
  if (!data.length) return;

  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers.map((header) => JSON.stringify(row[header] ?? "")).join(",")
  );

  const csvContent = [headers.join(","), ...rows].join("\n");

  downloadCsvFile(csvContent, filename);
}

/** Money from the market-price endpoints is in cents; shown as "$1,234.56". */
export function formatMarketPriceCents(cents: number): string {
  return parsePrice(centsToDollars(cents));
}

/** One RFC 4180 cell: always quoted, inner quotes doubled, so commas in a name stay in one column. */
function toCsvCell(value: string | number): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

/** CSV text from a header row plus data rows, every cell quoted. */
export function buildCsvContent(
  headers: string[],
  rows: (string | number)[][]
): string {
  return [headers, ...rows]
    .map((row) => row.map((cell) => toCsvCell(cell)).join(","))
    .join("\n");
}

export const MARKET_REVIEW_CSV_AGGREGATE_HEADERS = [
  "CPT Code",
  "Description",
  "Service Line",
  "Your Volume",
  "# Competitors",
  "Avg",
  "Median",
  "Min",
  "Max",
];

/**
 * CLE-2939 — the market review download, wide format: one row per CPT code with the aggregate
 * columns, then one column per competitor in `review.competitors` order. A competitor with no
 * price for a code gets a blank cell. Built as header + row arrays rather than objects so a
 * competitor whose name looks like a number, or matches an aggregate column, cannot reorder or
 * overwrite a column.
 */
export function buildMarketReviewCsv(
  review: Pick<IMarketPriceReviewResponse, "competitors" | "rows">,
  formatMoney: (cents: number) => string = formatMarketPriceCents
): { headers: string[]; rows: string[][] } {
  const headers = [
    ...MARKET_REVIEW_CSV_AGGREGATE_HEADERS,
    ...review.competitors,
  ];
  const rows = review.rows.map((row) => {
    const rateByCompetitor = new Map<string, number>();
    row.prices.forEach(({ competitorName, rate }) => {
      if (!rateByCompetitor.has(competitorName)) {
        rateByCompetitor.set(competitorName, rate);
      }
    });
    return [
      row.cptCode,
      row.description ?? "",
      row.serviceLine ?? "",
      String(row.volume ?? 0),
      String(row.competitorCount ?? 0),
      formatMoney(row.average),
      formatMoney(row.median),
      formatMoney(row.min),
      formatMoney(row.max),
      ...review.competitors.map((competitor) => {
        const rate = rateByCompetitor.get(competitor);
        return rate === undefined ? "" : formatMoney(rate);
      }),
    ];
  });
  return { headers, rows };
}

/**
 * CLE-2939 — the setting sub-tab to open first: the provider's own (comparison) setting when it
 * has data, else the first tab.
 */
export function pickInitialSettingIndex(
  settings: MarketPriceSetting[],
  comparisonSetting?: MarketPriceSetting | null
): number {
  if (!comparisonSetting) return 0;
  const index = settings.indexOf(comparisonSetting);
  return index >= 0 ? index : 0;
}

// Keys encode (payerId, termId) as "payerId:termId". Term ids from the backend
// contain colons (ISO timestamps in the synthetic id), so we must only split on
// the FIRST colon — anything after is the full term id.
export function parsePayerTermKey(key: string): {
  payerId: number;
  termId: string;
} {
  const idx = key.indexOf(":");
  if (idx < 0) return { payerId: Number(key), termId: "" };
  return { payerId: Number(key.slice(0, idx)), termId: key.slice(idx + 1) };
}

/**
 * CLE-2939 — the payer-dropdown value the comparison tab opens with when the user switches to it
 * with a payer selected on another tab: the exact term they last had for that payer on the
 * comparison tab, else `payerId:` (no term yet — `resolveCarriedPayerOption` picks one once the
 * options load). Null when no payer is carried.
 */
export function carriedComparisonPayerTermKey(
  carriedPayerId: number | null,
  lastComparisonKey: string | null
): string | null {
  if (carriedPayerId === null) return null;
  if (
    lastComparisonKey &&
    parsePayerTermKey(lastComparisonKey).payerId === carriedPayerId
  ) {
    return lastComparisonKey;
  }
  return `${carriedPayerId}:`;
}

interface IPayerOptionGroup {
  payerId: number;
  items: { value: string; isCurrent?: boolean }[];
}

/**
 * CLE-2939 — keeps a carried-over payer only when this setting has market data for it. `key` is
 * kept when it is one of the payer's options; otherwise the payer's current term, else its first
 * option. Null when the payer has no options here, which clears the dropdown.
 */
export function resolveCarriedPayerOption(
  key: string,
  groups: IPayerOptionGroup[]
): string | null {
  const { payerId } = parsePayerTermKey(key);
  const group = groups.find((g) => g.payerId === payerId);
  if (!group || group.items.length === 0) return null;
  if (group.items.some((item) => item.value === key)) return key;
  return (group.items.find((item) => item.isCurrent) ?? group.items[0]).value;
}

/** CLE-2939 — the payer survives a tab switch only when the new setting has data for it. */
export function keepPayerIfAvailable(
  payerId: number | null,
  availablePayerIds: number[]
): number | null {
  if (payerId === null) return null;
  return availablePayerIds.includes(payerId) ? payerId : null;
}
